import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Tooltip,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { AxiosRequestConfig } from "axios";
import { useEffect, useState } from "react";
import { APIS } from "../api/apis";
import { deleteOperation, get, getAxiosHeaders, patch } from "../shared/AxiosAPI";
import { Enquiry, EnquiryPage } from "../shared/types";
import { getStaticGrades } from "../shared/Util";
import { useNotify } from "../../../context/NotificationContext";
import PageTopBar from "../../../components/common/PageTopBar";
import { fieldHintTooltipProps, toolbarButtonSx, toolbarFieldsSx } from "../../../components/common/toolbarStyles";

export const Enquiries = () => {
  const notify = useNotify();
  const enquiryStates: string[] = ["INITIATED", "APPEARING_FOR_EE", "EE_HOLD", "EE_FAILED", "EE_PASSED", "ADMITTED"];
  const MESSAGE_VIEW = "MESSAGE_VIEW";
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [actionResMsg, setActionResMsg] = useState<string>();
  const [view, setView] = useState<string>();

  //Search filters
  const [nameInput, setNameInput] = useState<string>("");
  const [phoneNosInput, setPhoneNosInput] = useState<string>("");
  const [gradeFilter, setGradeFilter] = useState<string>("");
  const [stateFilter, setStateFilter] = useState<string>("");

  //Pagination
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [isApiCallReq, setIsApiCallReq] = useState<boolean>(false);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  //Row actions menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuEnquiry, setMenuEnquiry] = useState<Enquiry | null>(null);

  //Edit popup
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [editingEnquiry, setEditingEnquiry] = useState<Enquiry | null>(null);
  const [editState, setEditState] = useState<string>("");
  const [editError, setEditError] = useState<string | null>(null);
  const [editSubmitting, setEditSubmitting] = useState<boolean>(false);

  //Delete popup
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [deletingEnquiry, setDeletingEnquiry] = useState<Enquiry | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState<boolean>(false);

  const getForwardStates = (state: string): string[] => {
    const index = enquiryStates.indexOf(state);
    return index === -1 ? enquiryStates : enquiryStates.slice(index);
  };

  const openRowMenu = (e: React.MouseEvent<HTMLElement>, enquiry: Enquiry) => {
    setMenuAnchorEl(e.currentTarget);
    setMenuEnquiry(enquiry);
  };

  const closeRowMenu = () => {
    setMenuAnchorEl(null);
    setMenuEnquiry(null);
  };

  const openEditDialog = () => {
    if (menuEnquiry) {
      setEditingEnquiry(menuEnquiry);
      setEditState(menuEnquiry.state);
      setEditError(null);
      setEditDialogOpen(true);
    }
    closeRowMenu();
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setEditingEnquiry(null);
    setEditError(null);
    setEditSubmitting(false);
  };

  const submitEdit = () => {
    if (!editingEnquiry) return;
    setEditSubmitting(true);
    setEditError(null);
    const updatedEnquiry: Enquiry = { ...editingEnquiry, state: editState };
    patch(APIS.ENQUIRIES, null, [updatedEnquiry])
      .then((res) => {
        setEditSubmitting(false);
        if (res.status === 200) {
          const matchesFilter = stateFilter.length === 0 || updatedEnquiry.state === stateFilter;
          setEnquiries((prev) =>
            matchesFilter
              ? prev.map((e) => (e === editingEnquiry ? updatedEnquiry : e))
              : prev.filter((e) => e !== editingEnquiry)
          );
          closeEditDialog();
          notify("Enquiry updated successfully.", "success");
        }
      })
      .catch((err) => {
        setEditSubmitting(false);
        setEditError(err?.response?.data?.message || err?.message || String(err));
      });
  };

  const openDeleteConfirm = () => {
    if (menuEnquiry) {
      setDeletingEnquiry(menuEnquiry);
      setDeleteConfirmOpen(true);
    }
    closeRowMenu();
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setDeletingEnquiry(null);
    setDeleteSubmitting(false);
  };

  const confirmDelete = () => {
    if (!deletingEnquiry) return;
    setDeleteSubmitting(true);
    const cHeaders = getAxiosHeaders();
    const config: AxiosRequestConfig = { headers: cHeaders, data: deletingEnquiry };
    deleteOperation(APIS.ENQUIRIES, config)
      .then((res) => {
        setDeleteSubmitting(false);
        if (res.status === 200) {
          loadEnquiriesPage(page, rowsPerPage);
          closeDeleteConfirm();
          notify("Enquiry deleted successfully.", "success");
        }
      })
      .catch((err) => {
        setDeleteSubmitting(false);
        closeDeleteConfirm();
        notify(err?.response?.data?.message || "Failed to delete enquiry.", "error");
      });
  };

  const loadEnquiriesPage = (pageArg: number, sizeArg: number) => {
    const params = new URLSearchParams();
    if (phoneNosInput.length > 0) params.set("phoneNumbers", phoneNosInput);
    if (nameInput.length > 0) params.set("name", nameInput)
    if (gradeFilter.length > 0) params.set("grade", gradeFilter);
    if (stateFilter.length > 0) params.set("state", stateFilter);
    params.set("page", String(pageArg));
    params.set("size", String(sizeArg));

    let cHeaders = getAxiosHeaders();
    get(`${APIS.ENQUIRIES_SEARCH}?${params.toString()}`, cHeaders)
      .then((res) => {
        let enquiryPage: EnquiryPage = res.data;
        let content = enquiryPage.content ?? [];
        setEnquiries(content);
        setTotalElements(enquiryPage.totalElements ?? 0);
        if (content.length > 0) {
          setView("SHOW_ENQUIRIES");
        } else {
          setView(MESSAGE_VIEW);
          setActionResMsg("No Data Found");
        }
      })
      .catch((err) => {
        alert(err);
      });
  };

  const searchEnquiries = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (page !== 0) {
      setPage(0);
    } else {
      loadEnquiriesPage(0, rowsPerPage);
    }
  };

  useEffect(() => {
    if (isApiCallReq) {
      loadEnquiriesPage(page, rowsPerPage);
    } else {
      setIsApiCallReq(true);
    }
  }, [page, rowsPerPage]);

  return (
    <Box sx={{ mt: 2, mr: 2 }}>
      <PageTopBar
        left="Enquiries"
        right={
          <Box
            component="form"
            onSubmit={searchEnquiries}
            sx={{ ...toolbarFieldsSx, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}
          >
            <Tooltip title="Guardian phone number. For several, separate with commas: 98xxxxxxxx, 97xxxxxxxx" {...fieldHintTooltipProps}>
              <TextField
                label="Phone(s)"
                size="small"
                value={phoneNosInput}
                onChange={(e) => setPhoneNosInput(e.target.value)}
                sx={{ width: 160 }}
              />
            </Tooltip>
            <Tooltip title="Type part of the student's name, e.g. &quot;moh&quot; finds Mohammed" {...fieldHintTooltipProps}>
              <TextField
                label="Name"
                size="small"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                sx={{ width: 150 }}
              />
            </Tooltip>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel id="grade-label">Grade</InputLabel>
              <Select
                labelId="grade-label"
                label="Grade"
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value as string)}
              >
                <MenuItem value="">All</MenuItem>
                {getStaticGrades()?.map((grade: string) => (
                  <MenuItem value={grade} key={grade}>
                    {grade}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="state-label">State</InputLabel>
              <Select
                labelId="state-label"
                label="State"
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value as string)}
              >
                <MenuItem value="">All</MenuItem>
                {enquiryStates.map((state: string) => (
                  <MenuItem value={state} key={state}>
                    {state}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button type="submit" variant="outlined" size="small" sx={toolbarButtonSx}>
              Search
            </Button>
          </Box>
        }
      />

      <Box>
        {(view !== "SHOW_ENQUIRIES" || enquiries.length === 0) && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            {view === MESSAGE_VIEW || view === "SHOW_ENQUIRIES"
              ? "No enquiries match the search."
              : "Search by phone, name, grade or state, then click Search."}
          </Typography>
        )}
        {view === "SHOW_ENQUIRIES" && enquiries.length > 0 ? (
          <>
            <>
              <Grid>
                <TableContainer className="mui-table-container-style">
                  <Table
                    size="small"
                    className="mui-table-bordered"
                    sx={{
                      tableLayout: "auto"
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>#</strong>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <strong>Std Name</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Gender</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Grade</strong>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <strong>Created On</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Current School</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Guardian Name</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Mobile</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Actions</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {enquiries?.map((enquiry: Enquiry, index) => (
                        <TableRow key={enquiry.name + enquiry.guardianName + enquiry.createdOn + enquiry.mobileNo}>
                          <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{enquiry.name}</TableCell>
                          <TableCell>{enquiry.gender}</TableCell>
                          <TableCell>{enquiry.grade}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{enquiry.createdOn}</TableCell>
                          <TableCell>{enquiry.currentSchool}</TableCell>
                          <TableCell>{enquiry.guardianName}</TableCell>
                          <TableCell>{enquiry.mobileNo}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={(e) => openRowMenu(e, enquiry)}
                              disabled={enquiry.state === "ADMITTED"}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                    size="small"
                    sx={{
                      "& .MuiTablePagination-toolbar": {
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingLeft: 2,
                        paddingRight: 2,
                      },
                    }}
                  />
                </TableContainer>
              </Grid>
            </>
          </>
        ) : (
          ""
        )}
      </Box>

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeRowMenu}>
        <MenuItem onClick={openEditDialog}>Edit</MenuItem>
        <MenuItem onClick={openDeleteConfirm}>Delete</MenuItem>
      </Menu>

      <Dialog open={editDialogOpen} onClose={editSubmitting ? undefined : closeEditDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Enquiry</DialogTitle>
        <DialogContent>
          {editingEnquiry && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              <TextField label="Name" value={editingEnquiry.name} size="small" fullWidth InputProps={{ readOnly: true }} />
              <TextField label="Gender" value={editingEnquiry.gender} size="small" fullWidth InputProps={{ readOnly: true }} />
              <TextField label="Grade" value={editingEnquiry.grade} size="small" fullWidth InputProps={{ readOnly: true }} />
              <TextField label="Created On" value={editingEnquiry.createdOn} size="small" fullWidth InputProps={{ readOnly: true }} />
              <TextField label="Guardian Name" value={editingEnquiry.guardianName} size="small" fullWidth InputProps={{ readOnly: true }} />
              <TextField label="Mobile" value={editingEnquiry.mobileNo} size="small" fullWidth InputProps={{ readOnly: true }} />
              <FormControl size="small" fullWidth>
                <InputLabel id="edit-state-label">State</InputLabel>
                <Select
                  labelId="edit-state-label"
                  label="State"
                  value={editState}
                  onChange={(e) => setEditState(e.target.value as string)}
                >
                  {getForwardStates(editingEnquiry.state).map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {editError && <Alert severity="error">{editError}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={editSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={submitEdit}
            variant="contained"
            disabled={editSubmitting || !editingEnquiry || editState === editingEnquiry.state}
          >
            {editSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={deleteSubmitting ? undefined : closeDeleteConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Enquiry</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete the enquiry for {deletingEnquiry?.name}?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm} disabled={deleteSubmitting}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={deleteSubmitting}>
            {deleteSubmitting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
